import React, { useState, useRef, useLayoutEffect } from 'react';
import { motion } from 'framer-motion';
import { Box, Typography, useTheme } from '@mui/joy';

const AnimatedLineChart = ({ 
  data, 
  width = '100%', 
  height = 250, 
  color = '#6366f1',
  showPoints = true,
  title,
  subtitle,
  style
}) => {
  const theme = useTheme();
  const [hoveredPoint, setHoveredPoint] = useState(null);
  const containerRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(400);

  useLayoutEffect(() => {
    if (!containerRef.current) return;
    const handleResize = () => {
      setContainerWidth(containerRef.current.offsetWidth);
    };
    handleResize();
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Responsive chart area
  const chartW = containerWidth;
  const chartH = height;
  const paddingX = 32;
  const paddingY = 32;

  if (!data || data.length === 0) {
    return (
      <Box ref={containerRef} sx={{ width: '100%', height: chartH, ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', borderRadius: 2 }}>
        <Typography level="body-sm" color="neutral">No data available</Typography>
      </Box>
    );
  }

  const maxValue = Math.max(...data.map(d => d.value));
  const minValue = Math.min(...data.map(d => d.value));
  const range = maxValue - minValue || 1;

  const points = data.map((point, index) => ({
    x: (index / (data.length - 1)) * (chartW - 2 * paddingX) + paddingX,
    y: chartH - paddingY - ((point.value - minValue) / range) * (chartH - 2 * paddingY),
    originalData: point
  }));

  const pathData = points.map((point, index) => 
    `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`
  ).join(' ');

  return (
    <Box ref={containerRef} sx={{ width: '100%', height: chartH, ...style }}>
      {title && (
        <Box sx={{ mb: 1 }}>
          <Typography level="title-sm" sx={{ fontWeight: 500 }}>
            {title}
          </Typography>
          {subtitle && (
            <Typography level="body-xs" color="neutral" sx={{ mt: 0.5 }}>
              {subtitle}
            </Typography>
          )}
        </Box>
      )}
      <svg width={chartW} height={chartH} viewBox={`0 0 ${chartW} ${chartH}`} style={{ display: 'block', width: '100%', height: '100%' }}>
        {/* Line Path */}
        <motion.path
          d={pathData}
          stroke={color}
          strokeWidth={2}
          fill="none"
          strokeLinecap="round"
          strokeLinejoin="round"
          initial={{ pathLength: 0 }}
          animate={{ pathLength: 1 }}
          transition={{ duration: 1.2, ease: "easeInOut" }}
        />
        {/* Data Points */}
        {showPoints && points.map((point, index) => (
          <motion.g key={index}>
            {/* Invisible hit area for easier hover */}
            <circle
              cx={point.x}
              cy={point.y}
              r={16}
              fill="transparent"
              style={{ pointerEvents: 'all' }}
              onMouseEnter={() => setHoveredPoint(index)}
              onMouseLeave={() => setHoveredPoint(null)}
            />
            {/* Visible dot */}
            <motion.circle
              cx={point.x}
              cy={point.y}
              r={hoveredPoint === index ? 7 : 5}
              fill={hoveredPoint === index ? color : theme.palette.background.body}
              stroke={color}
              strokeWidth={2}
              style={{ pointerEvents: 'none' }}
              initial={{ scale: 0, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ duration: 0.3, delay: (index * 0.1) + 0.5, type: "spring", stiffness: 300 }}
              whileHover={{ scale: 1.5 }}
            />
            {/* Hover Tooltip */}
            {hoveredPoint === index && (
              <motion.g initial={{ opacity: 0, scale: 0.8 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.2 }} style={{ pointerEvents: 'none' }}>
                <rect
                  x={point.x - 30}
                  y={point.y - 45}
                  width={60}
                  height={25}
                  rx={6}
                  fill={theme.palette.neutral[900]}
                  opacity={0.9}
                />
                <text
                  x={point.x}
                  y={point.y - 30}
                  fontSize="11"
                  fill="white"
                  textAnchor="middle"
                  fontWeight="500"
                >
                  {point.originalData.value}
                </text>
              </motion.g>
            )}
          </motion.g>
        ))}
      </svg>
    </Box>
  );
};

export default AnimatedLineChart; 